import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import { API_URL, LIMIT } from "../../config/config.data";
import { entities } from "../../config/config.dataExplorer";
import { getData } from "../../store/dataSlice";

/**
 * Renders a cell for editing categorical data in a table via a dropdown.
 *
 * @param {Object} props - The component props.
 * @param {string} props.id - The ID of the row.
 * @param {string} props.field - The field name of the cell.
 * @param {Object} props.row - The data of the current row.
 * @param {Object} props.column - The column configuration.`
 * @returns {JSX.Element} - The rendered component.
 */
// CategoricalEditCell.js
const CategoricalEditCell = ({ id, field, row, column }) => {
  const {
    categoriesEndpoint,
    categoriesEndpointResponseKey,
    categoriesEndpointResponseOptionsKey,
    entityToUpdate,
    entityColumnToUpdate,
  } = column.renderEditCellProps;
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [options, setOptions] = useState([]);
  const [closing, setClosing] = useState(false); // for avoiding stale closure with options state
  const [newId, setNewId] = useState(null);
  const [optionValue, setOptionValue] = useState(
    row[column.renderEditCellProps?.entityColumnToUpdate] || "select-an-option"
  );

  // get the entity configuration object
  const entity = Object.values(entities).find(
    (entity) => entity.endpoint === categoriesEndpoint
  );

  // EFFECTS -------------------------------------------------------------------

  // fetch options on component mount
  useEffect(() => {
    const abortController = new AbortController();

    fetchOptions(abortController);

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // avoid stale closure with options state by communicating state change with
  //  closing state, then handle the dropdown change with freshly scoped options and newId
  useEffect(() => {
    if (closing) {
      handleDropdownChange({ target: { value: newId } });
      setClosing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closing]);

  // FUNCTIONS -----------------------------------------------------------------

  // fetch categorical options
  const fetchOptions = async (abortController) => {
    try {
      const response = await axios.get(
        `${API_URL}/${categoriesEndpoint}?limit=${LIMIT}`,
        {
          signal: abortController?.signal,
        }
      );
      const data = response.data[categoriesEndpointResponseKey];

      setOptions([
        {
          id: "select-an-option",
          label: <Typography>Select an option...</Typography>,
        },
        ...data.map((option) => ({
          id: option.id,
          label: option[categoriesEndpointResponseOptionsKey],
        })),
        {
          id: "add-new-option",
          label: (
            <Typography sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AddIcon sx={{ color: "primary.dark" }} />
              {`Create new ${column.headerName}`}{" "}
            </Typography>
          ),
        },
      ]);
    } catch (error) {
      console.error("Failed to fetch options:", error);
    } finally {
      setLoading(false);
    }
  };

  // update the selected option in the database
  const handleDropdownChange = async (event) => {
    setOptionValue(event.target.value);

    if (event.target.value === "add-new-option") {
      return;
    }
    let updatedRow = {};
    if (event.target.value === "select-an-option") {
      updatedRow = {
        ...row,
        [entityColumnToUpdate]: null,
      };
    } else {
      const selectedOption = options.find(
        (option) => option.id === event.target.value
      );
      const newValue = selectedOption?.id || null;

      // params.row contains the current row's data
      updatedRow = {
        ...row, // Spread all current row fields
        [entityColumnToUpdate]: newValue,
      };
    }

    const url = `${API_URL}/${entityToUpdate}/${id}`;

    try {
      axios.put(url, updatedRow).then((response) => {
        dispatch(getData({ dataId: entityToUpdate, source: "CategoricalEdit" }));
      });
    } catch (error) {
      console.error(`Failed to update ${categoriesEndpoint}`, error);
    }
  };

  // create a new categorical option
  const handleSubmitForm = async () => {
    if (!formData.name) {
      return;
    }

    const body = {
      ...formData,
    };

    try {
      axios.post(`${API_URL}/${categoriesEndpoint}`, body).then((response) => {
        axios
          .get(`${API_URL}/${categoriesEndpoint}?limit=${LIMIT}`)
          .then((response) => {
            const data = response.data[categoriesEndpointResponseKey];
            const id = data.sort((a, b) => b.id - a.id)[0].id;
            setNewId(id);
            setOptions([
              ...options.slice(0, options.length - 1),
              { id, label: formData.name },
              options[options.length - 1],
            ]);
            setFormData({});
            setClosing(true);
          });
      });
    } catch (error) {
      console.error(`Failed to create new ${categoriesEndpoint}`, error);
      // Handle error (e.g., show an error message
    }
  };

  // render the dropdown and dialog for adding a new option
  return loading ? (
    <CircularProgress size={24} color="warning" />
  ) : (
    <>
      <Select
        value={optionValue}
        onChange={handleDropdownChange}
        displayEmpty
        inputProps={{ "aria-label": "Without label" }}
        fullWidth
      >
        {options.map((option) => (
          <MenuItem
            key={option.id}
            value={option.id}
            sx={{
              fontWeight: option.id === optionValue ? "bold" : "normal",
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>

      {/* dialog where user can add a new categorical option */}
      <Dialog
        open={optionValue === "add-new-option"}
        onClose={() => setOptionValue("select-an-option")}
      >
        <DialogTitle>{`Create New ${column.headerName}`}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            id="name"
            label="Name"
            type="text"
            fullWidth
            onChange={(event) => setFormData({ name: event.target.value })}
          />
          {entity.requiredCreateFields?.map((field) => (
            <TextField
              key={field}
              margin="dense"
              id={field}
              label={field}
              type="text"
              fullWidth
              onChange={(event) =>
                setFormData({ ...formData, [field]: event.target.value })
              }
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button
            color="secondary"
            onClick={() => setOptionValue("select-an-option")}
          >
            Cancel
          </Button>
          <Button
            color="secondary"
            onClick={handleSubmitForm}
            autoFocus
            type="submit"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CategoricalEditCell;
